import React from 'react'
import { Badge, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, useToast } from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { OrganizationTypeItem, OrganizationTypeCreateUpdatePayload } from '../api/organizationTypesApi'
import { organizationTypesApi, LANGUAGES } from '../api/organizationTypesApi'

type Form = {
  code: string
  name: string
  language: string
  sortOrder: number
  isActive: boolean
}

export default function OrganizationTypesPage() {
  const { toast } = useToast()

  return (
    <CrudPage<OrganizationTypeItem, Form, OrganizationTypeCreateUpdatePayload, OrganizationTypeCreateUpdatePayload>
      title="Organizasyon Tipleri"
      subtitle="Organizasyon tiplerini dil bazlı yönetin (tr, en, az)"
      primaryActionLabel="Yeni Tip"
      list={async (p) =>
        organizationTypesApi.list({
          lang: p.filters.lang || 'tr',
          search: p.search || undefined,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as 'code' | 'name' | 'language' | 'sortOrder' | 'updatedAt') ?? undefined,
          sortDir: (p.sortDir as 'asc' | 'desc') ?? undefined,
        })
      }
      getRowId={(o) => o.id}
      columns={[
        {
          key: 'code',
          header: 'Kod',
          sortable: true,
          widthClassName: 'w-[120px]',
          render: (o) => <span className="font-medium">{o.code}</span>,
        },
        {
          key: 'name',
          header: 'Ad',
          sortable: true,
          widthClassName: 'w-[240px]',
          render: (o) => o.name,
        },
        {
          key: 'language',
          header: 'Dil',
          sortable: true,
          widthClassName: 'w-[100px]',
          render: (o) => o.language.toUpperCase(),
        },
        {
          key: 'isActive',
          header: 'Aktif',
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (o) =>
            o.isActive ? (
              <Badge variant="success">Aktif</Badge>
            ) : (
              <Badge variant="warning">Pasif</Badge>
            ),
        },
        {
          key: 'sortOrder',
          header: 'Sıra',
          sortable: true,
          widthClassName: 'w-[80px]',
          render: (o) => o.sortOrder,
        },
        {
          key: 'updatedAt',
          header: 'Güncellenme',
          sortable: true,
          widthClassName: 'w-[180px]',
          render: (o) =>
            o.updatedAt
              ? new Date(o.updatedAt).toLocaleString('tr-TR')
              : o.createdAt
                ? new Date(o.createdAt).toLocaleString('tr-TR')
                : '—',
        },
      ]}
      searchPlaceholder="Ara (kod, ad)…"
      filters={[
        {
          id: 'lang',
          label: 'Dil',
          value: 'tr',
          options: [
            { label: 'Türkçe', value: 'tr' },
            { label: 'English', value: 'en' },
            { label: 'Azərbaycan', value: 'az' },
          ],
          widthClassName: 'min-w-[140px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Organizasyon Tipi', edit: 'Organizasyon Tipini Düzenle' }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        language: 'tr',
        sortOrder: 0,
        isActive: true,
      })}
      getEditForm={(o) => ({
        code: o.code,
        name: o.name,
        language: o.language,
        sortOrder: o.sortOrder,
        isActive: o.isActive,
      })}
      validate={(f) => ({
        code: !f.code.trim() ? 'Kod zorunludur' : undefined,
        name: !f.name.trim() ? 'Ad zorunludur' : undefined,
        language: !f.language ? 'Dil seçiniz' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Kod"
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, code: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            required
            disabled={isSubmitting}
            placeholder="Örn: LTD, AS"
            leftIcon={<i className="material-symbols-outlined text-lg">tag</i>}
          />
          <Input
            label="Ad"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, name: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder="Organizasyon tipi adı"
            leftIcon={<i className="material-symbols-outlined text-lg">business</i>}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700">Dil</label>
            <Select
              value={form.language}
              onValueChange={(v: string) => setForm((s) => ({ ...s, language: v }))}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full" error={touched && !!errors.language}>
                <SelectValue placeholder="Dil seçin" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {touched && errors.language && (
              <p className="mt-1 text-sm text-red-600">{errors.language}</p>
            )}
          </div>
          <Input
            label="Sıra"
            type="number"
            value={String(form.sortOrder)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, sortOrder: parseInt(e.target.value, 10) || 0 }))
            }
            onBlur={() => setTouched(true)}
            disabled={isSubmitting}
            placeholder="0"
          />
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label="Aktif"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        language: f.language,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        language: f.language,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      create={(payload) => organizationTypesApi.create(payload)}
      update={(id, payload) => organizationTypesApi.update(Number(id), payload)}
      remove={(id) => organizationTypesApi.delete(Number(id))}
      getCreatedId={(res) => res?.id}
    />
  )
}
